import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { KnowledgeCenterHero } from './components/KnowledgeCenterHero';
import { SearchResultsPanel } from './components/SearchResultsPanel';
import { getSearchSeo, searchKnowledgeCenter } from './repository';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function KnowledgeCenterSearch() {
  const pageScope = useRef(null);
  const [searchParams] = useSearchParams();
  const queryParam = searchParams.get('q') || '';
  const hasQuery = Boolean(queryParam.trim());

  const results = useMemo(
    () => searchKnowledgeCenter(queryParam),
    [queryParam]
  );

  useGSAP(
    () => {
      const heroTimeline = gsap.timeline({ defaults: { ease: 'power2.out' } });
      heroTimeline
        .fromTo(
          '.kc-hero-title',
          { y: 40, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.7 }
        )
        .fromTo(
          '.kc-hero-subtitle',
          { y: 24, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55 },
          '-=0.35'
        )
        .fromTo(
          '.kc-hero-search',
          { y: 20, opacity: 0, scale: 0.98 },
          { y: 0, opacity: 1, scale: 1, duration: 0.5 },
          '-=0.25'
        )
        .fromTo(
          '.kc-hero-stat',
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4, stagger: 0.08 },
          '-=0.2'
        );

      ScrollTrigger.create({
        trigger: '.kc-search-results',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-search-summary, .kc-search-browse-categories, .kc-search-empty',
            { y: 16, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.45, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-search-tabs',
            { y: 12, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.4, ease: 'power2.out', delay: 0.05 }
          );
          gsap.fromTo(
            '.kc-search-result',
            { y: 18, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.35,
              stagger: { each: 0.02, from: 'start' },
              ease: 'power2.out',
              delay: 0.08,
            }
          );
        },
      });
    },
    { scope: pageScope, dependencies: [queryParam] }
  );

  return (
    <>
      <PageSeoHelmet {...getSearchSeo()} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-search-page">
          <KnowledgeCenterHero
            initialQuery={queryParam}
            inputId="kc-search-page"
          />
          <SearchResultsPanel results={results} hasQuery={hasQuery} />
        </PageMain>
        <Footer />
      </div>
    </>
  );
}
